import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { IDevice } from 'lavva.exalushome/build/js/Services/Devices/IDevice';
import { IDeviceChannel } from 'lavva.exalushome/build/js/Services/Devices/IDeviceChannel';
import { useLoggedUserStore } from '../../../store/logged-user';
import { useDevices } from '../../device-list/hooks/use-devices';
import { GroupedDevice } from '../../device-list/types';
import { GroupChannelsHookParams } from '../types';

export const SCENE_TYPE = 'Scene';

export const useGroupChannels = ({ items, setItems, visibility, withScenes }: GroupChannelsHookParams) => {
  const { t } = useTranslation('groups');
  const { groupId } = useParams<{ groupId: string }>();
  const { groupedDevices, isFetched } = useDevices({ hideAll: true, withScenes: withScenes });
  const canDisplaySceneDevices = useLoggedUserStore((s) => s.permissions.canDisplaySceneDevices);

  useEffect(() => {
    if (groupedDevices && !items.length) {
      const mappedDevices = groupedDevices.map((groupedDevice: GroupedDevice) => ({
        label: groupedDevice.label,
        type: groupedDevice.type,
        devices: groupedDevice.devices.map((x: IDevice) => ({
          device: x,
          channels: x.Channels.map((y: IDeviceChannel) => ({
            id: y.ChannelId,
            channel: y,
            checked: visibility ? !y.IsHidden : !!y.ChannelGroups.find((x) => x === groupId),
            defaultChecked: visibility ? !y.IsHidden : !!y.ChannelGroups.find((x) => x === groupId),
          })),
        })),
      }));

      setItems(mappedDevices);
    }
  }, [groupedDevices, visibility, groupId]);

  const totalChannels = useMemo(() => {
    return items
      .filter((item) => item.type !== SCENE_TYPE)
      .flatMap((item) => item.devices.flatMap((device) => device.channels));
  }, [items]);

  const totalScenes = useMemo(() => {
    return items
      .filter((item) => item.type === SCENE_TYPE)
      .flatMap((item) => item.devices.flatMap((device) => device.channels));
  }, [items]);

  const selectedChannelsCount = useMemo(() => totalChannels.filter((y) => y.checked).length, [totalChannels]);
  const selectedScenesCount = useMemo(() => totalScenes.filter((y) => y.checked).length, [totalScenes]);

  const handleChannelOnChange = (id: string, groupType: string) => {
    const updatedItems = [...items];

    const foundGroupIndex = updatedItems.findIndex((x) => x.type === groupType);

    if (foundGroupIndex !== -1) {
      const foundDeviceIndex = updatedItems[foundGroupIndex].devices.findIndex(
        (x) => x.device.Guid === id.split('_')[0],
      );

      if (foundDeviceIndex !== -1) {
        const foundChannelIndex = updatedItems[foundGroupIndex].devices[foundDeviceIndex].channels.findIndex(
          (x) => x.id === id,
        );

        if (foundChannelIndex !== -1) {
          updatedItems[foundGroupIndex].devices[foundDeviceIndex].channels[foundChannelIndex].checked =
            !updatedItems[foundGroupIndex].devices[foundDeviceIndex].channels[foundChannelIndex].checked;
        }

        setItems(updatedItems);
      }
    }
  };

  const tabs = [
    { label: t('channels') + ` (${selectedChannelsCount}/${totalChannels.length})` },
    ...(canDisplaySceneDevices ? [{ label: t('actions') + ` (${selectedScenesCount}/${totalScenes.length})` }] : []),
  ];

  return {
    handleChannelOnChange,
    tabs,
    items,
    isFetched,
  };
};
